import React, { useCallback, useEffect, useRef, useState } from 'react';
import NoData from '../../../ui/noData/NoData';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NotificationsSettings from '../NotificationsSettings/NotificationsSettings';
import get_insight_preferences_api_call from '../../../lib/api/tara/notifications/settings/get_insight_preferences_api_call';
import get_insight_delivery_options_api_call from '../../../lib/api/tara/notifications/settings/get_insight_delivery_options_api_call';
import {
  bellSVG,
  cart2SVG,
  cartAbandonedSVG,
  cartAddSVG,
  chartBarSVG,
  checkoutSVG,
  clockSVG,
  cursorSVG,
  dollarSVG,
  eyeSVG,
  halfStarSVG,
  targetSVG,
  trash2SVG,
  trendLineSVG,
  chatSquareSVG,
  dragSVG,
} from '../../../lib/allCustomSVGs';
import ReportsListItem from '../../../ui/listItems/ReportsListItem';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';

// ACTIVATED items (for list)
import get_all_activated_notification_items_api_call, {
  ActivatedItem,
} from '../../../lib/api/Reports/notifications/get_all_activated_notification_items_api_call';

// Enable/disable by type
import update_custom_graph_notification_api_call from '../../../lib/api/Reports/notifications/update_custom_graph_notification_api_call';
import update_custom_prompt_notification_api_call from '../../../lib/api/Reports/notifications/update_custom_prompt_notification_api_call';

// For building integrations & metrics (insights)
import get_tara_notifications_api_call from '../../../lib/api/tara/notifications/get_tara_notifications_api_call';

// For Add modal preload
import get_graphs_api_call, {
  GraphApi,
} from '../../../lib/api/Reports/report_graph_api_call';
import get_custom_prompts_api_call from '../../../lib/api/custom-prompts/custom-prompts-api-call';
import { CustomPrompt } from '../../../interfaces/CustomPrompt';
import update_insights_order_api_call, {
  delete_shop_insight_api_call,
} from '../../../lib/api/Reports/notifications/shop_insights_api_calls';

type Props = {
  refreshTrigger?: number;
  setIntegrationsAndMetrics: (data: any[]) => void;

  // NEW: let the parent (Notifications) know when Add modal can be opened
  setAddNotificationsReady: (ready: boolean) => void;
  setGraphsForAdd: (graphs: GraphApi[]) => void;
  setPromptsForAdd: (prompts: CustomPrompt[]) => void;
};

const ReportsNotifications = ({
  refreshTrigger,
  setIntegrationsAndMetrics,
  setAddNotificationsReady,
  setGraphsForAdd,
  setPromptsForAdd,
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('pending');
  const [allPreferences, setAllPreferences] = useState<any>({});
  const [allDeliveryOptions, setAllDeliveryOptions] = useState<any[]>([]);

  const [items, setItems] = useState<ActivatedItem[]>([]);
  const [isListLoading, setIsListLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('pending');
  const [deletingId, setDeletingId] = useState<string | number | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [isOrderUpdating, setIsOrderUpdating] = useState(false);
  const [orderUpdatingId, setOrderUpdatingId] = useState<string | null>(null);
  const previousItemsRef = useRef<ActivatedItem[]>([]);

  // Preload trackers for Add modal
  const [isInsightsOptionsLoaded, setIsInsightsOptionsLoaded] = useState(false);
  const [isGraphsLoaded, setIsGraphsLoaded] = useState(false);
  const [isPromptsLoaded, setIsPromptsLoaded] = useState(false);

  const allAddDepsReady =
    isInsightsOptionsLoaded && isGraphsLoaded && isPromptsLoaded;

  // Local raw stores to allow filtering against activated items
  const [insightsOptionsRaw, setInsightsOptionsRaw] = useState<any[]>([]);
  const [graphsRaw, setGraphsRaw] = useState<GraphApi[]>([]);
  const [promptsRaw, setPromptsRaw] = useState<CustomPrompt[]>([]);

  // icons for metrics (insights)
  const getIconByTitle = (title: string = '') => {
    const lower = title.toLowerCase();
    if (lower.includes('order')) return cart2SVG;
    if (lower.includes('view')) return eyeSVG;
    if (lower.includes('abandon')) return cartAbandonedSVG;
    if (lower.includes('add to cart')) return cartAddSVG;
    if (lower.includes('checkout')) return checkoutSVG;
    if (lower.includes('time')) return clockSVG;
    if (lower.includes('top')) return halfStarSVG;
    if (lower.includes('click')) return cursorSVG;
    if (lower.includes('engagement')) return cursorSVG;
    if (lower.includes('conversion')) return targetSVG;
    if (lower.includes('aov') || lower.includes('average order value'))
      return dollarSVG;
    if (lower.includes('cpm') || lower.includes('impression'))
      return chartBarSVG;
    if (lower.includes('revenue') || lower.includes('sales')) return dollarSVG;
    return bellSVG;
  };

  const sanitizeShortCode = (sc?: string | null) =>
    (sc || '').replace(/^\s*\{\{\s*/, '').replace(/\s*\}\}\s*$/, '');

  /* ---------------------------------------------------------
   * Load INSIGHTS options (raw, unfiltered)
   * ------------------------------------------------------- */
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const allData = await get_tara_notifications_api_call();
        if (Array.isArray(allData) && allData.length > 0) {
          const grouped = allData.reduce(
            (acc: Record<string, any>, curr: any) => {
              const slug = curr?.mcp_info?.slug || curr?.mcp || 'other';
              const name = curr?.mcp_info?.name || curr?.mcp || 'Other';
              const icon = curr?.mcp_info?.icon || '';

              if (!acc[slug]) {
                acc[slug] = {
                  id: slug,
                  mainIcon: icon,
                  mainTitle: name,
                  subTitle: '',
                  items: [],
                };
              }

              acc[slug].items.push({
                id: curr.id,
                icon: getIconByTitle(curr.metric),
                title: curr.metric,
                value: sanitizeShortCode(curr.short_code) || curr.metric, // short-code
                subTitle: curr.description || '',
                tag: curr?.shop_config?.frequency
                  ? curr.shop_config.frequency
                  : 'none',
                isEnabled: curr.is_activated,
                shortCode: curr.short_code,
                type: 'insight',
              });

              return acc;
            },
            {} as Record<string, any>
          );

          const groupedArr = Object.values(grouped || {});
          setInsightsOptionsRaw(groupedArr); // store raw
        } else {
          setInsightsOptionsRaw([]);
        }
      } catch (err) {
        console.error('Error fetching notification options for insights:', err);
        setInsightsOptionsRaw([]);
      } finally {
        setIsInsightsOptionsLoaded(true);
      }
    };
    fetchNotifications();
  }, []);

  /* ---------------------------------------------------------
   * Load GRAPHS and PROMPTS (raw, unfiltered)
   * ------------------------------------------------------- */
  useEffect(() => {
    const loadGraphs = async () => {
      try {
        const list = await get_graphs_api_call();
        setGraphsRaw(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Error preloading graphs:', e);
        setGraphsRaw([]);
      } finally {
        setIsGraphsLoaded(true);
      }
    };
    const loadPrompts = async () => {
      try {
        const list = await get_custom_prompts_api_call();
        setPromptsRaw(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Error preloading prompts:', e);
        setPromptsRaw([]);
      } finally {
        setIsPromptsLoaded(true);
      }
    };

    loadGraphs();
    loadPrompts();
  }, []);

  /* ---------------------------------------------------------
   * Activated list (shown items) – used to FILTER options
   * ------------------------------------------------------- */
  const fetchActivatedList = async () => {
    setIsListLoading('pending');
    try {
      const res = await get_all_activated_notification_items_api_call();
      if (res) {
        setItems(res.items);
        setIsListLoading('success');
      } else {
        setItems([]);
        setIsListLoading('null');
      }
    } catch (e) {
      console.error('Error fetching activated items', e);
      setIsListLoading('error');
    }
  };

  useEffect(() => {
    fetchActivatedList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Inform parent when all deps are ready
  useEffect(() => {
    setAddNotificationsReady(allAddDepsReady);
  }, [allAddDepsReady, setAddNotificationsReady]);

  /* ---------------------------------------------------------
   * FILTER raw options by activated items, and push to parent
   * ------------------------------------------------------- */
  useEffect(() => {
    // Build quick lookup sets for active IDs by type
    const activeInsightIds = new Set(
      items.filter((i) => i.type === 'insight').map((i) => String(i.id))
    );
    const activeGraphIds = new Set(
      items.filter((i) => i.type === 'graph').map((i) => String(i.id))
    );
    const activePromptIds = new Set(
      items.filter((i) => i.type === 'prompt').map((i) => String(i.id))
    );

    // 1) Filter insights options
    const filteredInsights = (insightsOptionsRaw || []).map((group) => ({
      ...group,
      items: (group.items || []).filter(
        (it: any) => !activeInsightIds.has(String(it.id))
      ),
    }));
    setIntegrationsAndMetrics(filteredInsights);

    // 2) Filter graphs list
    const filteredGraphs = (graphsRaw || []).filter(
      (g) => !activeGraphIds.has(String(g.id))
    );
    setGraphsForAdd(filteredGraphs);

    // 3) Filter prompts list
    const filteredPrompts = (promptsRaw || []).filter(
      (p) => !activePromptIds.has(String(p.id))
    );
    setPromptsForAdd(filteredPrompts);
  }, [
    items,
    insightsOptionsRaw,
    graphsRaw,
    promptsRaw,
    setIntegrationsAndMetrics,
    setGraphsForAdd,
    setPromptsForAdd,
  ]);

  /* ---------------------------------------------------------
   * Settings & Delivery options (unchanged)
   * ------------------------------------------------------- */
  useEffect(() => {
    const fetchSettingsAndFrequencies = async () => {
      setIsLoading('pending');
      try {
        const [preferences, deliveryOptions] = await Promise.all([
          get_insight_preferences_api_call(),
          get_insight_delivery_options_api_call(),
        ]);
        if (typeof preferences === 'object' && Array.isArray(deliveryOptions)) {
          setAllPreferences(preferences);
          setAllDeliveryOptions(deliveryOptions);
          setIsLoading('success');
        } else {
          setIsLoading('null');
        }
      } catch (err) {
        console.error('Error fetching settings/frequencies:', err);
        setIsLoading('error');
      }
    };
    fetchSettingsAndFrequencies();
  }, []);

  // delete handler by type
  const handleDelete = async (item: ActivatedItem) => {
    if (deletingId) return;
    setDeletingId(item.id);
    try {
      if (item.type === 'insight') {
        // New DELETE endpoint
        await delete_shop_insight_api_call(item.id);
      } else if (item.type === 'graph') {
        await update_custom_graph_notification_api_call(item.id, false);
      } else if (item.type === 'prompt') {
        await update_custom_prompt_notification_api_call(item.id, false);
      }
      await fetchActivatedList();
    } catch (e) {
      console.error('Failed to delete notification item', e);
    } finally {
      setDeletingId(null);
    }
  };

  const reorderItems = useCallback(
    (
      list: ActivatedItem[],
      sourceId: string,
      destinationId: string
    ): ActivatedItem[] | null => {
      if (sourceId === destinationId) return null;
      const sourceIndex = list.findIndex(
        (item) => String(item.id) === sourceId
      );
      const destinationIndex = list.findIndex(
        (item) => String(item.id) === destinationId
      );
      if (sourceIndex === -1 || destinationIndex === -1) return null;

      const working = [...list];
      const [moved] = working.splice(sourceIndex, 1);
      let insertIndex = working.findIndex(
        (item) => String(item.id) === destinationId
      );

      if (insertIndex === -1) return null;
      if (sourceIndex < destinationIndex) {
        insertIndex += 1;
      }

      working.splice(insertIndex, 0, moved);
      return working;
    },
    []
  );

  const resetDragState = useCallback(() => {
    setDraggingId(null);
    setDragOverId(null);
  }, []);

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>, startId: string) => {
      if (isOrderUpdating) {
        event.preventDefault();
        return;
      }
      previousItemsRef.current = [...items];
      setDraggingId(startId);
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', startId);
    },
    [isOrderUpdating, items]
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>, targetId: string) => {
      event.preventDefault();
      if (!draggingId || draggingId === targetId) return;
      setDragOverId(targetId);
    },
    [draggingId]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },
    []
  );

  const handleDragLeave = useCallback(
    (_event: React.DragEvent<HTMLDivElement>, targetId: string) => {
      if (dragOverId === targetId) {
        setDragOverId(null);
      }
    },
    [dragOverId]
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>, targetId: string) => {
      event.preventDefault();
      event.stopPropagation();
      if (isOrderUpdating) {
        resetDragState();
        return;
      }
      const sourceId = draggingId || event.dataTransfer.getData('text/plain');
      if (!sourceId || sourceId === targetId) {
        resetDragState();
        return;
      }

      const updated = reorderItems(items, sourceId, targetId);
      if (!updated) {
        resetDragState();
        return;
      }

      setItems(updated);
      setIsOrderUpdating(true);
      setOrderUpdatingId(sourceId);

      try {
        await update_insights_order_api_call(
          updated.map((item) => `${item.type}_${item.id}`)
        );
      } catch (error) {
        console.error('Failed to update order', error);
        setItems(previousItemsRef.current);
      } finally {
        setIsOrderUpdating(false);
        setOrderUpdatingId(null);
        resetDragState();
      }
    },
    [draggingId, isOrderUpdating, items, reorderItems, resetDragState]
  );

  const handleDragEnd = useCallback(() => {
    resetDragState();
  }, [resetDragState]);

  const renderListContent = () => {
    if (isListLoading === 'pending') {
      return (
        <div className='containerWithPaddingTopOnMobile'>
          <LoadingSpinner />
        </div>
      );
    } else if (isListLoading === 'error') {
      return (
        <div className='containerWithPaddingTopOnMobile'>
          <NoData
            title={t('ErrorFetchingData')}
            subTitle={t('SomethingWentWrong-msg')}
          />
        </div>
      );
    } else if (items.length === 0) {
      return (
        <div className='containerWithPaddingTopOnMobile'>
          <NoData
            title={t('NoItems')}
            subTitle={t('NoNotificationsConfigured')}
          />
        </div>
      );
    } else if (isListLoading === 'success' && items.length > 0) {
      return (
        <>
          {items.map((it) => {
            const isDeleting = deletingId === it.id;
            const title = it.name || it.short_code || String(it.id);
            const subTitle =
              it.type === 'insight'
                ? (it as any)?.mcp || (it as any)?.tara_mcp_slug || 'Insight'
                : it.type === 'graph'
                ? 'Graph'
                : 'Prompt';

            const icon =
              it.type === 'graph'
                ? trendLineSVG
                : it.type === 'prompt'
                ? chatSquareSVG
                : getIconByTitle(title);

            const itemId = String(it.id);
            const isItemDragging = draggingId === itemId;
            const isItemDragOver =
              dragOverId === itemId && draggingId !== itemId;

            return (
              <ReportsListItem
                key={`${it.type}-${it.id}`}
                id={String(it.id)}
                icon={icon}
                iconColor='#5573f7'
                title={title}
                subTitle={subTitle}
                onConfigClick={() => !isDeleting && handleDelete(it)}
                configIcon={isDeleting ? null : trash2SVG}
                configIconColor='#FF4B4B'
                {...(isDeleting && { configLoading: true })}
                // dragIcon={dragSVG}
                // dragIconColor='#A1A8C3'
                // dragLoading={orderUpdatingId === itemId}
                // isDragDisabled={
                //   isOrderUpdating ||
                //   Boolean(deletingId) ||
                //   Boolean(orderUpdatingId)
                // }
                // isDragging={isItemDragging}
                // isDragOver={isItemDragOver}
                // onDragStart={(event) => handleDragStart(event, itemId)}
                // onDragEnter={(event) => handleDragEnter(event, itemId)}
                // onDragOver={handleDragOver}
                // onDragLeave={(event) => handleDragLeave(event, itemId)}
                // onDrop={(event) => handleDrop(event, itemId)}
                // onDragEnd={handleDragEnd}
              />
            );
          })}
        </>
      );
    } else {
      return (
        <div>
          <NoData
            title={t('NoItems')}
            subTitle={t('NoNotificationsConfigured')}
          />
        </div>
      );
    }
  };

  // Render list items
  return (
    <>
      {isLoading === 'pending' ? (
        <div className='containerWithPaddingTopOnMobile'>
          <LoadingSpinner />
        </div>
      ) : null}
      {isLoading === 'error' ? (
        <div className='containerWithPaddingTopOnMobile'>
          <NoData
            title={t('ErrorFetchingData')}
            subTitle={t('SomethingWentWrong-msg')}
          />
        </div>
      ) : null}

      {isLoading === 'success' ? (
        <div className='containerWithPaddingTopOnMobile'>
          <SettingsCommonCard
            mainTitle={t('SelectedItems')}
            subTitle={t('ItemsYouHaveSelectedForNotifications')}
            titleFontSize='2rem'
            titleMarginBottom='0.5rem'
            childrenStyles={{
              marginTop: '2rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            {renderListContent()}
          </SettingsCommonCard>

          <div style={{ marginTop: '2rem' }}></div>

          <NotificationsSettings
            allPreferences={allPreferences}
            allDeliveryOptions={allDeliveryOptions}
            setAllPreferences={setAllPreferences}
            setAllDeliveryOptions={setAllDeliveryOptions}
          />
        </div>
      ) : null}
    </>
  );
};

export default ReportsNotifications;
